import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useMessageGroupAdminMutations } from "../hooks/useMessageGroupAdminMutations";
import { MessageGroupSummary } from "../messageGroups.types";
import { getAvatarColor, getGroupInitials } from "../utils/messageGroupDisplay.utils";
import { MemberPicker, PickableMember, memberKeyOf } from "./MemberPicker";

const MIN_MEMBERS = 2;

interface CreateGroupModalProps {
  open: boolean;
  companyId?: number | string | null;
  onClose: () => void;
  onCreated?: (group: MessageGroupSummary) => void;
}

export const CreateGroupModal = ({ open, companyId, onClose, onCreated }: CreateGroupModalProps) => {
  const [step, setStep] = useState(0);
  const [members, setMembers] = useState<PickableMember[]>([]);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [description, setDescription] = useState("");

  const { createGroup } = useMessageGroupAdminMutations(undefined, companyId);

  const canContinue = members.length >= MIN_MEMBERS;
  const canCreate = canContinue && name.trim().length > 0 && !createGroup.isPending;
  const previewName = name.trim() || "New group";
  const errorMessage = (createGroup.error as any)?.response?.data?.message ?? (createGroup.error as Error)?.message;

  const memberSummary = useMemo(() => members.map((member) => member.name).join(", "), [members]);

  const reset = () => {
    setStep(0);
    setMembers([]);
    setName("");
    setAvatar("");
    setDescription("");
    createGroup.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    const created = await createGroup.mutateAsync({
      name: name.trim(),
      avatar: avatar.trim() || null,
      description: description.trim() || null,
      members: members.map((member) => ({ type: member.type, uid: member.uid })),
    });

    onCreated?.(created);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={{ zIndex: 2147483000 }}>
      <DialogTitle sx={{ pb: 0.5, fontSize: 17, fontWeight: 700 }}>New group</DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
        <Stepper activeStep={step} alternativeLabel>
          <Step>
            <StepLabel>Members</StepLabel>
          </Step>
          <Step>
            <StepLabel>Details</StepLabel>
          </Step>
        </Stepper>

        {step === 0 ? (
          <>
            <MemberPicker selected={members} onChange={setMembers} />
            <Typography sx={{ fontSize: 11.5, color: canContinue ? "text.secondary" : "warning.main" }}>
              {canContinue
                ? `${members.length} people selected`
                : `Pick at least ${MIN_MEMBERS} people besides you — for two, use a direct chat.`}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={avatar.trim() || undefined}
                sx={{ width: 52, height: 52, fontWeight: 700, bgcolor: avatar.trim() ? undefined : getAvatarColor(previewName) }}
              >
                {avatar.trim() ? null : getGroupInitials(previewName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
                  {previewName}
                </Typography>
                <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }} title={memberSummary}>
                  {members.length + 1} members · {memberSummary}
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Group name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Photo URL (optional)"
              value={avatar}
              onChange={(event) => setAvatar(event.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
            />
          </>
        )}

        {errorMessage && (
          <Typography sx={{ fontSize: 12, color: "error.main" }}>{errorMessage}</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={step === 0 ? handleClose : () => setStep(0)} size="small" color="inherit">
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step === 0 ? (
          <Button onClick={() => setStep(1)} size="small" variant="contained" disabled={!canContinue}>
            Next
          </Button>
        ) : (
          <Button onClick={handleCreate} size="small" variant="contained" disabled={!canCreate}>
            {createGroup.isPending ? "Creating..." : "Create group"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export { memberKeyOf };
